import '../../App.css'

const About = () => {
    return (
        <div className="git-metrics-info px-4 py-4">
            <h1>Git Metrics</h1>
            <p>Gitmetrics is a comprehensive web application designed to provide detailed metrics and insights into your Git repositories. With Gitmetrics, you can easily track and visualize key performance indicators (KPIs) such as commits, pull requests, lines of code (LOC) changes, and more. The application supports repositories from Azure DevOps, Bitbucket, and GitHub, offering seamless integration with these popular version control systems.
                <br></br>
                Whether you are a developer, project manager, or team lead, Gitmetrics gives you in-depth analytics to better understand individual and team contributions, optimize development workflows, and drive continuous improvement in your projects.</p>
            <h2>Key Features</h2>
            <ul>
                <li><strong>Commit Insights</strong></li>
                <p>  Track the commits made by each developer within a given date range, including detailed information such as:
                    <br></br>
                    - Lines of Code (LOC) changed (Added, Modified, Deleted)
                    <br></br>
                    - Number of distinct files changed in each commit
                    <br></br>
                    - Commit messages and files affected
                    <br></br>
                    - Commit hash information for deep analysis
                    <br></br>
                    - Since individual commits within feature branches are eventually merged into other branches, merge commits are excluded from the calculation of Lines of Code (LOC).
                    <br></br>
                    - if a file is deleted, its LOC will not be included in the total calculation. Deleted lines will be shown in parentheses under the #LOC: Deleted field.</p>
                <li><strong>Pull Request (PR) Analytics</strong></li>
                <p>  Visualize and analyze the activity around pull requests, including:
                    <br></br>
                    - Merged PRs, Open PRs, and Declined PRs
                    <br></br>
                    - Detailed PR information, including title, source branch, destination branch, comments, and reviewer details
                    <br></br>
                    - Pull request comments, with the ability to display code-level comments and code blocks if available</p>
                <li><strong>Code-Level Insights</strong></li>
                <p> - Fetch and display code-level comments within PRs to help developers easily identify important changes and discussions.
                    <br></br>
                    - Track the impact of each commit and PR on the overall codebase, with granular details on how files have changed and how much code has been modified.</p>
                <li><strong>Repository Integration</strong></li>
                <p>  Gitmetrics supports integration with multiple repositories, including:</p>
                - Azure DevOps
                <br></br>
                - Bitbucket
                <br></br>
                - GitHub
                <p> This allows for a unified view of commit and PR metrics across different platforms.</p>
                <li><strong>Custom Date Range</strong></li>
                <p>Fetch commit and PR data for a specific date range, allowing for tailored reporting and analysis based on any given time period.</p>
                <li><strong>Developer Contribution Insights</strong></li>
                <p>Gain visibility into the contributions of individual developers, including how many commits they’ve made, the amount of code they’ve added or modified, and the number of files they’ve touched.</p>
                <li><strong>Seamless UI for Data Presentation</strong></li>
                <p>  The application provides an intuitive and easy-to-navigate user interface to visualize the fetched data. Graphs, tables, and detailed views allow for a comprehensive understanding of project and developer performance.</p>
                <li><strong>Real-Time Updates</strong></li>
                <p>Keep track of ongoing development activities in real time, ensuring that your metrics are always up to date and reflecting the latest changes in the codebase</p>
            </ul>
            <h2>Home section</h2>
            <hr></hr>
            <ul>
                <h4>Search Options</h4>
                <ul>
                    <ul>
                        <li><strong>Platform</strong>: Select the DevOps platform (e.g., Azure DevOps).</li>
                        <li><strong>Repository</strong>: Choose the repository to view Git metrics for.</li>
                        <li><strong>Developers</strong>: Select specific developers (or all) whose data you want to analyze.</li>
                        <li><strong>Start Date / End Date</strong>: Select a time range to filter metrics.</li>
                    </ul>
                </ul>
                <h4>Grid</h4>
                <ul>
                    <p>The table displays developer-wise activity based on the selected filters.</p>
                    <p>Displays summary stats per developer, including:</p>
                    <ul>
                        <li><strong>Developer Name</strong>: The name of the developer.</li>
                        <li><strong>#Commits</strong>: Total number of commits made by the developer.</li>
                        <li><strong>#Files Changed</strong>: Total number of files changed in that commit</li>
                        <li><strong>#LOC: Added</strong>: Total lines of code added in all commits.</li>
                        <li><strong>#LOC: Modified</strong>: Total lines of code Modified in all commits..</li>
                        <li><strong>#LOC: Deleted</strong>: Total lines of code removed. Values in brackets indicate total deletions of lines across all deleted files.</li>
                        <li><strong>#LOC: Total</strong>: Sum of added and removed lines.</li>
                        <li><strong>Merged PRs</strong>: Number of Pull Requests (PRs) successfully merged.</li>
                        <li><strong>Declined PRs</strong>: Number of PRs that were declined or closed without merging.</li>
                        <li><strong>Open PRs</strong>: Number of currently open PRs.</li>
                        <li><strong>PR Comments</strong>: Number of comments made on PRs by the developer.</li>
                    </ul>

                    <h5>Commit Click Action</h5>
                    <ul>
                        <p>Clicking on the number under the # Commits column for any developer opens a detailed popup window showing commit-level insights for the selected developer.</p>
                        <ul>
                            <li><strong>Commit Hash</strong>: Unique identifier (SHA) for the commit.</li>
                            <li><strong>#LOC: Added</strong>: Number of lines of code added in that specific commit.</li>
                            <li><strong>#LOC: Modified</strong>: Total lines of code Modified in that specific commits.</li>
                            <li><strong>#LOC: Deleted</strong>: Number of lines removed.</li>
                            <li><strong>#LOC: Total</strong>: Sum of added and removed lines for the commit.</li>
                            <li><strong>Message</strong>: The commit message describing the changes.</li>
                            <li><strong>Date</strong>: Date when the commit was made.</li>
                            <li><strong>View</strong>: Option to view more details about the commit (opening a view for filewise LOC changes).</li>
                        </ul>

                        <h5>Commit Hash Click Action</h5>

                        <p>Each Commit Hash is clickable. Clicking the Commit Hash redirects the user to the corresponding commit page, allowing for direct access to the commit details.</p>
                    </ul>

                    <h5>Merged PR Click Action</h5>
                    <ul>
                        <p>Clicking on a number in the Merged PRs column displays a detailed list of merged pull requests.</p>
                        <ul>
                            <li><strong>PR ID</strong>: ID of PR.</li>
                            <li><strong>PR title</strong>: Name of PR.</li>
                            <li><strong>Source branch</strong>: Branch to merge from.</li>
                            <li><strong>Destination branch</strong>: Branch to merge into.</li>
                            <li><strong>Comment</strong>: Number of comments</li>
                            <li><strong>Reviewer(s)</strong>: Reviewers of PR</li>
                        </ul>

                        <h5>PR ID Click Action</h5>
                        <ul>
                            <p>Each PR ID is clickable. Clicking the ID redirects the user to the corresponding pull request page, allowing for direct access to the PR discussion, code changes, and approval history.</p>
                        </ul>
                        <h5>PR Comment Count Click Action</h5>
                        <ul>
                            <p>Clicking the number in the PR Comments column opens a popup listing:</p>
                            <ul>
                                <li>All individual comments made on that developer’s PRs</li>
                            </ul>
                        </ul>

                    </ul>
                </ul>

                <h4>Charts</h4>
                <ul>
                    <h5>Commits & PRs Count By Developers</h5>
                    <ul>
                        <p>Displays bar chart showing distribution of commits and pull requests (PRs) by developer.</p>
                        <ul>
                            <li><strong>Total Commits</strong>: Bar showing how many commits each developer made.</li>
                            <li><strong>Total PRs</strong>: Bar showing how many PRs each developer made.</li>
                        </ul>
                    </ul>
                    <br></br>
                    <h5>Developer Contribution Overview</h5>
                    <ul>
                        <p>Displays pie charts showing distribution of commits and pull requests (PRs) by developer.</p>
                        <ul>
                            <li><strong>Commits by Developer</strong>: Pie chart showing how many commits each developer made.</li>
                            <li><strong>PRs by Developer</strong>: Pie chart showing number of PRs created per developer.</li>
                            <li>Each color segment represents a different developer, with their name and count displayed on hover.</li>
                        </ul>
                    </ul>
                </ul>
                <br></br>
                <h2>Reports Section</h2>
                <hr></hr>
                <br></br>
                <ul>
                    <p>The Reports dropdown provides three visual analytics options for understanding activity trends across developers over a selected date range.</p>
                    <h4>Commit History</h4>
                    <ul>
                        X-axis: Date range (based on selected start and end date).
                        <br></br>
                        Y-axis: Number of commits on that day.
                    </ul>
                    <ul>
                        <h5>Line Chart</h5>
                        <p>Displays a line chart visualizing daily commit activity by each developer.</p>
                        <ul>
                            <li>Each line represents a different developer.</li>
                            <li>Useful for identifying productivity trends, team engagement, and peak contribution days.</li>
                        </ul>
                        <h5>Stacked Bar chart</h5>
                        <p>Display a Stacked Bar chart visualizing daily commit by each developer.</p>
                        <ul>
                            <li>Each bar represents total number of Commits made on a date.</li>
                            <li>The Stacks in Bar represents the Developer and mentions the count of commits made by the developer inside the stack</li>
                        </ul>
                    </ul>
                    <h4>PR History</h4>
                    <ul>
                        X-axis: Date range (based on selected start and end date).
                        <br></br>
                        Y-axis: Number of PRs raised per day.
                    </ul>
                    <ul>
                        <h5>Line Chart</h5>
                        <p>Displays a line chart similar to commit history but for Pull Requests.</p>
                        <ul>
                            <li>Each line represents an individual developer.</li>
                            <li>Helps analyze PR patterns, frequency, and peak collaboration periods.</li>
                        </ul>
                        <h5>Stacked Bar chart</h5>
                        <p>Display a Stacked Bar chart visualizing daily PRs by each developer.</p>
                        <ul>
                            <li>Each bar represents total number of PRs made on a date.</li>
                            <li>The Stacks in Bar represents the Developer and mentions the count of Pr made by the developer inside the stack</li>
                        </ul>
                    </ul>
                </ul>
            </ul>
        </div >
    );
};

export default About;